import type { DataType, FieldsGroups } from "@/types/api/fields"

export type Query =
  | "find"
  | "list"
  | "listMy"

function getActionName (actionType: string, entityName: string): string {
  if (!entityName)
    throw new Error("Не указано имя сущности для генерации запроса")

  const entityNameFirstUpcased =
    entityName[0].toUpperCase() + entityName.slice(1)

  return `${actionType}${entityNameFirstUpcased}`
}

export function getFieldSchema (dataType: DataType): string {
  let schema = ""

  switch (dataType.type) {
    case "status":
      schema = "{ id }"
      break

    case "tag":
      schema = "{ id title { ru en } }"
      break

    case "translatable":
      schema = "{ ru en }"
      break

    case "upload":
      schema = "{ path url }"
      break

    case "address":
      schema = "{ result postalCode region area city settlement street stead house block flat longitude latitude }"
      break

    case "array": {
      const arrayType = dataType.options.of?.type

      schema = getFieldSchema({
        type: arrayType as DataType["type"],
        options: {},
      })

      break
    }
  }

  return schema
}

// Генерация запроса для получения всех полей сущности
export function generateQuery ({
  queryType,
  queryName,
  entityName,
  formSchema,
  ignoreId = false,
  ignorePublic = false,
  additionalFields = {},
}: {
  queryType: Query
  queryName?: string
  entityName: string
  formSchema: FieldsGroups
  ignoreId?: boolean
  ignorePublic?: boolean
  additionalFields?: Record<string, string>
}): string {
  queryName ||= getActionName(queryType, entityName)

  let fields: Record<string, string> = {}

  for (const group of formSchema)
    for (const field of group.fields)
      if (ignorePublic || field.public)
        fields[field.alias] = getFieldSchema(field.dataType)

  fields = {
    ...fields,
    // Избегаем дубликатов полей
    ...additionalFields,
    id: "",
    attributes: "",
  }

  const fieldsAsStr = Object.entries(fields)
    .map(([alias, schema]) => `${alias}${schema ? ` ${schema}` : ""}`)
    .join(`\n${" ".repeat(6)}`)

  let result = ""

  switch (queryType) {
    case "find":
      result = /* gql */ `
query ($id: UUID) {
  ${queryName} (id: $id) {
    ${fieldsAsStr}
  }
}
      `
      break

    case "list":
    case "listMy":
      result = /* gql */ `
query ($id: UUID) {
  ${queryName} (
    limit: 1
    ${ignoreId ? "" : "filters: [{ name: \"id\" value: $id }]"}
  ) {
    items {
      ${fieldsAsStr}
    }
  }
}
      `
      break
  }

  return result.trim()
}

// Генерация мутации для создания/обновления сущности
export function generateMutation ({
  mutationName,
  formSchema,
  additionalFields,
  resultFields = {},
}: {
  mutationName: string
  formSchema: FieldsGroups
  additionalFields?: Record<string, string>
  resultFields?: Record<string, string>
}): string {
  let fields: Record<string, string> = {}

  for (const group of formSchema)
    for (const field of group.fields)
      if (!field.readOnly)
        fields[field.alias] = field.graphQLInputType

  fields = {
    ...fields,
    ...additionalFields,
    id: "UUID",
    attributes: "AnyObject",
  }

  const fieldsEntries = Object.entries(fields)

  const resultFieldsAsStr = Object.entries(resultFields)
    .map(([alias, schema]) => `${alias}${schema ? ` ${schema}` : ""}`)
    .join(`\n${" ".repeat(4)}`)

  const result = /* gql */ `
mutation (
  ${fieldsEntries
    .map(([alias, fType]) => `$${alias}: ${fType}`)
    .join("\n  ")}
) {
  ${mutationName} (
    ${fieldsEntries
    .map(([alias]) => `${alias}: $${alias}`)
    .join("\n    ")}
  )
    ${resultFieldsAsStr ? `{ ${resultFieldsAsStr} }` : ""}
}
  `

  return result.trim()
}
